import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  Divider,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import HistoryIcon from "@mui/icons-material/History";
import RestoreIcon from "@mui/icons-material/Restore";

import API from "../../utils/Axios.utils";
import useAuth from "../../hooks/useAuth";

const PROMPT_TYPES = [
  {
    key: "EMAIL_JOB_EXTRACTION",
    label: "Auto Job Creation (Email)",
    color: "#6366F1",
    bgColor: "#EEF2FF",
    desc: "Extraction instructions for incoming requisition emails & attachments",
    variables: [
      { token: "{title}", desc: "Job Title" },
      { token: "{coreSkill}", desc: "Primary Core Competency" },
      { token: "{techSkills}", desc: "Secondary Tech Skills" },
      { token: "{experience}", desc: "Years of Experience" },
      { token: "{location}", desc: "Location / Work Mode" },
      { token: "{budget}", desc: "Compensation / Budget" },
      { token: "{openings}", desc: "Headcount / Openings" },
      { token: "{notice_period}", desc: "Notice Period in Days" },
    ],
  },
  {
    key: "CANDIDATE_SCORING",
    label: "Candidate Scoring",
    color: "#0284C7",
    bgColor: "#F0F9FF",
    desc: "Instructions & evaluation weights for scoring resumes against jobs",
    variables: [
      { token: "{job_title}", desc: "Target Job Title" },
      { token: "{core_skills}", desc: "Required Core Skills" },
      { token: "{tech_skills}", desc: "Required Tech Stack" },
      { token: "{experience_required}", desc: "Required Seniority" },
      { token: "{candidate_resume}", desc: "Parsed Resume Content" },
    ],
  },
  {
    key: "CV_SEARCH",
    label: "CV Search / Matching",
    color: "#0D9488",
    bgColor: "#F0FDFA",
    desc: "Relevance rules & synonym matching for candidate database search",
    variables: [
      { token: "{search_query}", desc: "Recruiter Query or Role" },
      { token: "{mandatory_skills}", desc: "Must-have Skills" },
      { token: "{candidate_pool}", desc: "Candidate Profiles" },
    ],
  },
];

const PromptManagement = () => {
  const { user, organisationId, customerId } = useAuth();

  // Static Organisation and Customer Names from Session context
  const currentOrgName =
    user?.organisation_name ||
    user?.org_name ||
    (organisationId ? `Organisation #${organisationId}` : "Arabic");

  const currentCustomerName =
    user?.customer_name ||
    user?.cust_name ||
    (customerId ? `Customer #${customerId}` : "ArabicCustomer");

  const [prompts, setPrompts] = useState([]);
  const [defaultTemplates, setDefaultTemplates] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Email, 2: Scoring, 3: CV Search
  const [searchTerm, setSearchTerm] = useState("");

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    prompt_type: "EMAIL_JOB_EXTRACTION",
    prompt_name: "",
    prompt_description: "",
    prompt_text: "",
  });

  // AI Copilot State
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generatedRule, setGeneratedRule] = useState("");

  // Version History State
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyPrompt, setHistoryPrompt] = useState(null);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const promptInputRef = useRef(null);

  // Fetch prompts and templates
  const fetchData = async () => {
    setLoading(true);
    try {
      const [promptsRes, templatesRes] = await Promise.all([
        API.get("/api/prompts").catch(() => ({ data: { prompts: [] } })),
        API.get("/api/prompts/templates/default").catch(() => ({ data: { templates: {} } })),
      ]);

      const promptList = promptsRes.data?.prompts || promptsRes.data || [];
      const templates = templatesRes.data?.templates || templatesRes.data || {};

      setPrompts(promptList);
      setDefaultTemplates(templates);
    } catch (err) {
      console.error("Error loading prompt management data:", err);
      showNotification("Failed to load prompt configurations", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Filtered prompts
  const filteredPrompts = useMemo(() => {
    let list = [...prompts];

    // Filter by Tab Type
    if (activeTab === 1) list = list.filter((p) => p.prompt_type === "EMAIL_JOB_EXTRACTION");
    else if (activeTab === 2) list = list.filter((p) => p.prompt_type === "CANDIDATE_SCORING");
    else if (activeTab === 3) list = list.filter((p) => p.prompt_type === "CV_SEARCH");

    // Search filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (p) =>
          p.prompt_name?.toLowerCase().includes(q) ||
          p.prompt_description?.toLowerCase().includes(q) ||
          p.customer_name?.toLowerCase().includes(q) ||
          p.organisation_name?.toLowerCase().includes(q)
      );
    }

    return list;
  }, [prompts, activeTab, searchTerm]);

  // Handle open create dialog
  const handleOpenCreate = () => {
    const currentType =
      activeTab === 1
        ? "EMAIL_JOB_EXTRACTION"
        : activeTab === 2
        ? "CANDIDATE_SCORING"
        : activeTab === 3
        ? "CV_SEARCH"
        : "EMAIL_JOB_EXTRACTION";
    const defaultTemplate = defaultTemplates[currentType] || {};

    setFormData({
      prompt_type: currentType,
      prompt_name: defaultTemplate.prompt_name || "",
      prompt_description: defaultTemplate.prompt_description || "",
      prompt_text: defaultTemplate.prompt_text || "",
    });
    setAiInstruction("");
    setGeneratedRule("");
    setIsEditing(false);
    setSelectedPrompt(null);
    setDialogOpen(true);
  };

  // Handle open edit dialog
  const handleOpenEdit = (prompt) => {
    setFormData({
      prompt_type: prompt.prompt_type,
      prompt_name: prompt.prompt_name || "",
      prompt_description: prompt.prompt_description || "",
      prompt_text: prompt.prompt_text || "",
      organisation_name: prompt.organisation_name || currentOrgName,
      customer_name: prompt.customer_name || currentCustomerName,
    });
    setAiInstruction("");
    setGeneratedRule("");
    setIsEditing(true);
    setSelectedPrompt(prompt);
    setDialogOpen(true);
  };

  // AI Copilot: Generate Rule from User Request
  const handleGenerateRule = async () => {
    if (!aiInstruction.trim()) {
      showNotification("Please enter an instruction for the AI Copilot", "warning");
      return;
    }

    setAiGenerating(true);
    setGeneratedRule("");
    try {
      const res = await API.post("/api/prompts/ai-assist", {
        instruction: aiInstruction.trim(),
        prompt_type: formData.prompt_type,
        current_text: formData.prompt_text,
      });

      if (res.data?.generated_rule) {
        setGeneratedRule(res.data.generated_rule);
        showNotification("AI rule generated successfully!", "success");
      } else {
        showNotification("No rule was generated. Please try rephrasing.", "warning");
      }
    } catch (err) {
      console.error("AI Assist error:", err);
      const msg = err.response?.data?.message || err.message || "Failed to generate AI rule";
      showNotification(msg, "error");
    } finally {
      setAiGenerating(false);
    }
  };

  // Insert generated rule into editor
  const handleInsertGeneratedRule = () => {
    if (!generatedRule.trim()) return;
    const current = formData.prompt_text || "";
    const separator = current.endsWith("\n\n") ? "" : current.endsWith("\n") ? "\n" : "\n\n";
    const newPromptText = current + separator + generatedRule.trim();
    setFormData((prev) => ({
      ...prev,
      prompt_text: newPromptText,
    }));
    showNotification("Rule appended to prompt instructions", "success");
  };

  // Handle view prompt details
  const handleOpenView = (prompt) => {
    setSelectedPrompt(prompt);
    setViewDialogOpen(true);
  };

  // Toggle active status
  const handleToggleStatus = async (prompt) => {
    const newStatus = prompt.is_active === "Y" ? "N" : "Y";
    try {
      await API.put(`/api/prompts/${prompt.id}`, { is_active: newStatus });
      setPrompts((prev) =>
        prev.map((p) => (p.id === prompt.id ? { ...p, is_active: newStatus } : p))
      );
      showNotification(`Prompt ${newStatus === "Y" ? "activated" : "deactivated"} successfully`);
    } catch (err) {
      console.error("Failed to toggle prompt status:", err);
      showNotification("Failed to update status", "error");
    }
  };

  // Open Version History Dialog
  const handleOpenHistory = async (prompt) => {
    setHistoryPrompt(prompt);
    setSelectedVersion(null);
    setHistoryDialogOpen(true);
    setLoadingHistory(true);
    try {
      const res = await API.get(`/api/prompts/${prompt.id}/versions`);
      const versions = res.data?.versions || [];
      setHistoryVersions(versions);
      if (versions.length > 0) {
        setSelectedVersion(versions[0]);
      }
    } catch (err) {
      console.error("Failed to load prompt versions:", err);
      showNotification("Failed to load version history", "error");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Rollback to specific version
  const handleRollback = async (versionObj) => {
    if (!historyPrompt || !versionObj) return;
    const confirmRollback = window.confirm(
      `Are you sure you want to restore prompt "${historyPrompt.prompt_name}" to version v${versionObj.version}? This will create a new version snapshot and set it active.`
    );
    if (!confirmRollback) return;

    setRollingBack(true);
    try {
      const res = await API.post(`/api/prompts/${historyPrompt.id}/rollback/${versionObj.version}`);
      showNotification(res.data?.message || `Restored to v${versionObj.version} successfully`, "success");
      setHistoryDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error("Rollback error:", err);
      const msg = err.response?.data?.message || err.message || "Failed to restore version";
      showNotification(msg, "error");
    } finally {
      setRollingBack(false);
    }
  };

  // Populate default baseline template
  const handleLoadDefaultTemplate = () => {
    const tpl = defaultTemplates[formData.prompt_type];
    if (tpl) {
      setFormData((prev) => ({
        ...prev,
        prompt_name: prev.prompt_name || tpl.prompt_name,
        prompt_description: prev.prompt_description || tpl.prompt_description,
        prompt_text: tpl.prompt_text,
      }));
      showNotification("Baseline template loaded into editor");
    }
  };

  // Insert token into editor
  const handleInsertToken = (token) => {
    const currentText = formData.prompt_text || "";
    setFormData((prev) => ({
      ...prev,
      prompt_text: currentText + (currentText.endsWith("\n") || !currentText ? "" : " ") + token,
    }));
  };

  // Save Prompt (Create or Edit)
  const handleSavePrompt = async () => {
    if (!formData.prompt_name.trim()) {
      showNotification("Please provide a prompt name", "warning");
      return;
    }
    if (!formData.prompt_text.trim()) {
      showNotification("Prompt text instructions cannot be empty", "warning");
      return;
    }

    setSaving(true);
    try {
      if (isEditing && selectedPrompt) {
        await API.put(`/api/prompts/${selectedPrompt.id}`, {
          prompt_name: formData.prompt_name,
          prompt_description: formData.prompt_description,
          prompt_text: formData.prompt_text,
        });
        showNotification("Prompt updated successfully");
      } else {
        await API.post("/api/prompts", {
          prompt_type: formData.prompt_type,
          prompt_name: formData.prompt_name,
          prompt_description: formData.prompt_description,
          prompt_text: formData.prompt_text,
          customer_id: customerId || undefined,
        });
        showNotification("New prompt configured successfully");
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to save prompt";
      showNotification(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const currentTypeConfig =
    PROMPT_TYPES.find((t) => t.key === formData.prompt_type) || PROMPT_TYPES[0];

  // Token estimate: ~4 chars per token (standard LLM heuristic, capped at 4096)
  const estimatedTokens = Math.min(
    4096,
    Math.ceil((formData.prompt_text || "").length / 4)
  );

  return (
    <Box sx={{ p: 1 }}>
      {/* Header Paper */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <SmartToyIcon sx={{ color: "#2563EB", fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700} color="#0F172A">
              AI Prompt Management
            </Typography>
            <Chip
              label={`${currentOrgName} • ${currentCustomerName}`}
              size="small"
              sx={{
                bgcolor: "#EFF6FF",
                color: "#1D4ED8",
                fontWeight: 600,
                fontSize: "12px",
                border: "1px solid #BFDBFE",
              }}
            />
          </Box>
          <Typography variant="body2" color="#64748B">
            Configure system prompts, evaluation rubrics, and extraction guidelines for your organization and workspace.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchData}
            startIcon={<RefreshIcon />}
            sx={{ textTransform: "none", color: "#475569", borderColor: "#CBD5E1" }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleOpenCreate}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
              fontWeight: 600,
              px: 2,
            }}
          >
            + Create New Prompt
          </Button>
        </Stack>
      </Paper>

      {/* Main Content Area */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #E5E7EB" }}>
        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ minHeight: "44px" }}
          >
            <Tab
              label={`All Prompts (${prompts.length})`}
              sx={{ textTransform: "none", fontWeight: 600, minHeight: "44px" }}
            />
            <Tab
              label={`Auto Job Creation through Email (${
                prompts.filter((p) => p.prompt_type === "EMAIL_JOB_EXTRACTION").length
              })`}
              sx={{ textTransform: "none", fontWeight: 600, minHeight: "44px" }}
            />
            <Tab
              label={`Candidate Scoring (${
                prompts.filter((p) => p.prompt_type === "CANDIDATE_SCORING").length
              })`}
              sx={{ textTransform: "none", fontWeight: 600, minHeight: "44px" }}
            />
            <Tab
              label={`CV Search (${
                prompts.filter((p) => p.prompt_type === "CV_SEARCH").length
              })`}
              sx={{ textTransform: "none", fontWeight: 600, minHeight: "44px" }}
            />
          </Tabs>
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "center", bgcolor: "#FAFAFA" }}>
          <TextField
            size="small"
            placeholder="Search prompts by name or description…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#94A3B8" }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 320, maxWidth: 500, bgcolor: "#FFFFFF" }}
          />
        </Box>

        {/* Prompts Table */}
        <TableContainer>
          <Table sx={{ minWidth: 700 }}>
            <TableHead sx={{ bgcolor: "#F8FAFC" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Prompt Name & Description</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: "#475569" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading prompt configurations…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredPrompts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" fontWeight={600} color="#64748B">
                      No prompt templates found
                    </Typography>
                    <Typography variant="body2" color="#94A3B8" sx={{ mt: 0.5, mb: 2 }}>
                      Create a new prompt to view active system prompts.
                    </Typography>
                    <Button variant="outlined" size="small" onClick={handleOpenCreate} startIcon={<AddIcon />}>
                      Create Prompt
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPrompts.map((prompt) => {
                  const typeMeta =
                    PROMPT_TYPES.find((t) => t.key === prompt.prompt_type) || PROMPT_TYPES[0];

                  return (
                    <TableRow key={prompt.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight={700} color="#1E293B">
                              {prompt.prompt_name}
                            </Typography>
                            {prompt.prompt_description && (
                              <Typography variant="caption" color="#64748B" display="block">
                                {prompt.prompt_description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={typeMeta.label}
                          size="small"
                          sx={{
                            bgcolor: typeMeta.bgColor,
                            color: typeMeta.color,
                            fontWeight: 600,
                            fontSize: "12px",
                            border: `1px solid ${typeMeta.color}30`,
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={`v${prompt.version || 1}`}
                          size="small"
                          sx={{
                            bgcolor: "#F8FAFC",
                            color: "#64748B",
                            fontWeight: 600,
                            fontSize: "11px",
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Switch
                            size="small"
                            checked={prompt.is_active === "Y"}
                            onChange={() => handleToggleStatus(prompt)}
                            color="success"
                          />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color={prompt.is_active === "Y" ? "#16A34A" : "#94A3B8"}
                          >
                            {prompt.is_active === "Y" ? "Active" : "Inactive"}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View Prompt Text">
                            <IconButton
                              size="small"
                              aria-label="View Prompt Text"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenView(prompt);
                              }}
                              sx={{ color: "#64748B" }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Version History">
                            <IconButton
                              size="small"
                              aria-label="Version History"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenHistory(prompt);
                              }}
                              sx={{ color: "#7C3AED" }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Prompt">
                            <IconButton
                              size="small"
                              aria-label="Edit Prompt"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEdit(prompt);
                              }}
                              sx={{ color: "#2563EB" }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create / Edit Dialog (Matching Acceler8CX Modal Design) */}
      <Dialog
        open={dialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {isEditing ? "Edit Category Prompt" : "Create Category Prompt"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Prompts provide the AI instructions, while job requisition details & candidate data are passed dynamically.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Row 1: Static Organisation Name & Customer Name (Matching Acceler8CX Screenshot) */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField
                label="Organisation Name *"
                size="small"
                fullWidth
                value={
                  isEditing && selectedPrompt?.organisation_name
                    ? selectedPrompt.organisation_name
                    : currentOrgName
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-root": { bgcolor: "#F8FAFC" },
                  "& .MuiInputBase-input": { color: "#334155", fontWeight: 500 },
                }}
              />

              <TextField
                label="Customer Name *"
                size="small"
                fullWidth
                value={
                  isEditing && selectedPrompt?.customer_name
                    ? selectedPrompt.customer_name
                    : currentCustomerName
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-root": { bgcolor: "#F8FAFC" },
                  "& .MuiInputBase-input": { color: "#334155", fontWeight: 500 },
                }}
              />
            </Box>

            {/* Row 2: Category Chips Selection (Matching Acceler8CX Chip Design) */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="#475569" sx={{ mb: 1, display: "block" }}>
                Category *
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {PROMPT_TYPES.map((type) => {
                  const isSelected = formData.prompt_type === type.key;
                  return (
                    <Chip
                      key={type.key}
                      label={type.label}
                      icon={isSelected ? <CheckIcon fontSize="small" /> : undefined}
                      disabled={isEditing}
                      onClick={() => {
                        if (isEditing) return;
                        const tpl = defaultTemplates[type.key] || {};
                        setFormData((prev) => ({
                          ...prev,
                          prompt_type: type.key,
                          prompt_name: prev.prompt_name || tpl.prompt_name || "",
                          prompt_description: prev.prompt_description || tpl.prompt_description || "",
                          prompt_text: prev.prompt_text ? prev.prompt_text : tpl.prompt_text || "",
                        }));
                      }}
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        cursor: isEditing ? "default" : "pointer",
                        bgcolor: isSelected ? type.bgColor : "#F8FAFC",
                        color: isSelected ? type.color : "#64748B",
                        border: isSelected ? `2px solid ${type.color}` : "1px solid #E2E8F0",
                        transition: "all 0.15s ease-in-out",
                        "&:hover": {
                          bgcolor: isSelected ? type.bgColor : "#F1F5F9",
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Row 3: Prompt Name */}
            <TextField
              label="Prompt Name *"
              size="small"
              fullWidth
              placeholder="e.g. Standard Email Requisition Extraction"
              value={formData.prompt_name}
              onChange={(e) => setFormData((prev) => ({ ...prev, prompt_name: e.target.value }))}
            />

            {/* Row 4: Prompt Description */}
            <TextField
              label="Description (Optional)"
              size="small"
              fullWidth
              placeholder="Notes on what this prompt specializes in…"
              value={formData.prompt_description}
              onChange={(e) => setFormData((prev) => ({ ...prev, prompt_description: e.target.value }))}
            />

            {/* Row 4.5: AI Prompt Copilot (Natural Language Rule Generator) */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                background: "linear-gradient(135deg, #F0F7FF 0%, #F5F3FF 100%)",
                border: "1px solid #BFDBFE",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <AutoAwesomeIcon sx={{ color: "#2563EB", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700} color="#1E3A8A">
                  ✨ AI Prompt Copilot (Natural Language Assistant)
                </Typography>
                <Chip
                  label="Groq In-App AI"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "10px",
                    fontWeight: 700,
                    bgcolor: "#DBEAFE",
                    color: "#1E40AF",
                  }}
                />
              </Box>
              <Typography variant="caption" color="#475569" sx={{ display: "block", mb: 1.5 }}>
                Describe what rule, priority, or override you want in plain English. The internal AI maps it to Talent Engine's exact database schemas (e.g. assignment_duration_days, notice_period, techSkills).
              </Typography>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder='e.g. "Assign Assignment Duration (Days) as 555 for testing" or "Prioritize AWS in techSkills"'
                  value={aiInstruction}
                  onChange={(e) => setAiInstruction(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !aiGenerating) {
                      e.preventDefault();
                      handleGenerateRule();
                    }
                  }}
                  disabled={aiGenerating}
                  sx={{
                    bgcolor: "#FFFFFF",
                    "& .MuiOutlinedInput-root": {
                      borderColor: "#93C5FD",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleGenerateRule}
                  disabled={aiGenerating || !aiInstruction.trim()}
                  startIcon={aiGenerating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                  sx={{
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: "#2563EB",
                    "&:hover": { bgcolor: "#1D4ED8" },
                    px: 2.5,
                  }}
                >
                  {aiGenerating ? "Generating…" : "Generate Rule"}
                </Button>
              </Box>

              {/* Generated Rule Preview & Insertion */}
              {generatedRule && (
                <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px dashed #BFDBFE" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                    <Typography variant="caption" fontWeight={700} color="#1E40AF">
                      Generated Rule Directive:
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          navigator.clipboard.writeText(generatedRule);
                          showNotification("Generated rule copied to clipboard");
                        }}
                        startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
                        sx={{ textTransform: "none", fontSize: "11px", color: "#475569" }}
                      >
                        Copy
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setGeneratedRule("")}
                        sx={{ textTransform: "none", fontSize: "11px", color: "#94A3B8" }}
                      >
                        Clear
                      </Button>
                    </Stack>
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: "#FFFFFF",
                      border: "1px solid #CBD5E1",
                      borderRadius: 1.5,
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: "12px",
                      lineHeight: 1.5,
                      color: "#0F172A",
                      whiteSpace: "pre-wrap",
                      mb: 1.5,
                    }}
                  >
                    {generatedRule}
                  </Paper>

                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleInsertGeneratedRule}
                    startIcon={<AddIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: "#16A34A",
                      "&:hover": { bgcolor: "#15803D" },
                    }}
                  >
                    + Append to Prompt Instructions
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Row 5: System Prompt Instructions (with Tokens Counter in Header) */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600} color="#475569">
                  System Prompt Instructions *
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: estimatedTokens > 3800 ? "#DC2626" : "#64748B",
                  }}
                >
                  tokens: {estimatedTokens}/4096
                </Typography>
              </Box>

              <TextField
                inputRef={promptInputRef}
                multiline
                minRows={10}
                maxRows={20}
                fullWidth
                value={formData.prompt_text}
                onChange={(e) => setFormData((prev) => ({ ...prev, prompt_text: e.target.value }))}
                placeholder="Enter prompt instructions for the AI engine…"
                sx={{
                  "& .MuiInputBase-root": {
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: "13px",
                    lineHeight: 1.5,
                    bgcolor: "#FFFFFF",
                  },
                }}
              />
            </Box>

            {/* Row 6: User Prompt Template & Variables (Matching Acceler8CX layout) */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={700} color="#1E293B">
                  User Prompt Template
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleLoadDefaultTemplate}
                  sx={{ textTransform: "none", fontSize: "11px", color: "#2563EB" }}
                >
                  Reset to Baseline
                </Button>
              </Box>

              <Typography variant="caption" color="#64748B" display="block" sx={{ mb: 1.5 }}>
                Use &#123;variable_name&#125; syntax to inject dynamic email fields.
              </Typography>

              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {currentTypeConfig.variables.map((v) => (
                  <Chip
                    key={v.token}
                    label={`${v.token} (${v.desc})`}
                    size="small"
                    onClick={() => handleInsertToken(v.token)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: "#FFFFFF",
                      border: "1px solid #CBD5E1",
                      fontSize: "11px",
                      "&:hover": { bgcolor: "#EFF6FF", borderColor: "#93C5FD" },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none", color: "#64748B" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePrompt}
            disabled={saving}
            sx={{
              textTransform: "none",
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
              px: 3,
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditing ? (
              "Save Changes"
            ) : (
              "Save Prompt"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Prompt Modal */}
      <Dialog
        open={viewDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setViewDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {selectedPrompt?.prompt_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Organisation: {selectedPrompt?.organisation_name || currentOrgName} | Customer:{" "}
              {selectedPrompt?.customer_name || currentCustomerName} | Version: v{selectedPrompt?.version || 1}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setViewDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 1.5,
              whiteSpace: "pre-wrap",
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: "13px",
              color: "#1E293B",
              lineHeight: 1.6,
              maxHeight: "500px",
              overflowY: "auto",
            }}
          >
            {selectedPrompt?.prompt_text}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (selectedPrompt) {
                navigator.clipboard.writeText(selectedPrompt.prompt_text);
                showNotification("Prompt copied to clipboard");
              }
            }}
            startIcon={<ContentCopyIcon />}
            sx={{ textTransform: "none" }}
          >
            Copy Text
          </Button>
          <Button onClick={() => setViewDialogOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setHistoryDialogOpen(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HistoryIcon sx={{ color: "#7C3AED" }} />
              <Typography variant="h6" fontWeight={700} color="#0F172A">
                Version History: {historyPrompt?.prompt_name}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Organisation: {historyPrompt?.organisation_name || currentOrgName} | Customer:{" "}
              {historyPrompt?.customer_name || currentCustomerName} | Active: v{historyPrompt?.version || 1}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHistoryDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2.5 }}>
          {loadingHistory ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
              <CircularProgress size={36} sx={{ color: "#7C3AED" }} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2.5, minHeight: "450px" }}>
              {/* Left Pane: Version List */}
              <Box sx={{ borderRight: { md: "1px solid #E2E8F0" }, pr: { md: 2 } }}>
                <Typography variant="caption" fontWeight={700} color="#64748B" textTransform="uppercase" letterSpacing={0.5} sx={{ mb: 1.5, display: "block" }}>
                  Revision Timeline ({historyVersions.length})
                </Typography>
                <Stack spacing={1.5} sx={{ maxHeight: "450px", overflowY: "auto", pr: 0.5 }}>
                  {historyVersions.map((v) => {
                    const isSelected = selectedVersion?.version === v.version;
                    return (
                      <Paper
                        key={v.version}
                        elevation={0}
                        onClick={() => setSelectedVersion(v)}
                        sx={{
                          p: 1.5,
                          cursor: "pointer",
                          borderRadius: 2,
                          border: isSelected ? "2px solid #7C3AED" : "1px solid #E2E8F0",
                          bgcolor: isSelected ? "#F5F3FF" : "#FFFFFF",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: isSelected ? "#7C3AED" : "#C4B5FD",
                            bgcolor: isSelected ? "#F5F3FF" : "#FAF5FF",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Chip
                              label={`v${v.version}`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "11px",
                                height: "20px",
                                bgcolor: v.is_current ? "#DCFCE7" : "#F1F5F9",
                                color: v.is_current ? "#15803D" : "#475569",
                              }}
                            />
                            {v.is_current && (
                              <Chip
                                label="ACTIVE"
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "9px",
                                  height: "18px",
                                  bgcolor: "#16A34A",
                                  color: "#FFFFFF",
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="#94A3B8" fontSize="11px">
                            {new Date(v.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="#475569" fontWeight={500} display="block" sx={{ mb: 0.5 }}>
                          {v.change_summary || "Prompt instructions update"}
                        </Typography>
                        <Typography variant="caption" color="#94A3B8" fontSize="11px">
                          By: {v.author_name || "Admin"}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>

              {/* Right Pane: Selected Version Preview */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {selectedVersion ? (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} color="#0F172A">
                          Viewing Version v{selectedVersion.version}
                        </Typography>
                        <Typography variant="caption" color="#64748B">
                          Saved on {new Date(selectedVersion.created_at).toLocaleString()} • {selectedVersion.author_name}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedVersion.prompt_text);
                            showNotification("Version text copied to clipboard");
                          }}
                          startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
                          sx={{ textTransform: "none", fontSize: "12px" }}
                        >
                          Copy
                        </Button>
                        {!selectedVersion.is_current && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={rollingBack}
                            onClick={() => handleRollback(selectedVersion)}
                            startIcon={rollingBack ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              textTransform: "none",
                              fontSize: "12px",
                              fontWeight: 600,
                              bgcolor: "#7C3AED",
                              "&:hover": { bgcolor: "#6D28D9" },
                            }}
                          >
                            {rollingBack ? "Restoring…" : `Restore to v${selectedVersion.version}`}
                          </Button>
                        )}
                      </Stack>
                    </Box>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: 1.5,
                        whiteSpace: "pre-wrap",
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: "12.5px",
                        color: "#0F172A",
                        lineHeight: 1.6,
                        flex: 1,
                        maxHeight: "390px",
                        overflowY: "auto",
                      }}
                    >
                      {selectedVersion.prompt_text}
                    </Paper>
                  </>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, color: "#94A3B8" }}>
                    <Typography variant="body2">Select a version on the left to preview.</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #E2E8F0" }}>
          <Button onClick={() => setHistoryDialogOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Toast */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PromptManagement;
